import { useState, useRef } from 'react';
import { Form, Input, InputNumber, Select, Checkbox, Radio, Switch, Typography, Space, Empty, Divider, Tag, Tooltip, InputRef } from 'antd';
import { Filter, Box, Layers, Trash2, Container, Info, ChevronDown, ChevronRight, Check, GitPullRequest, Search } from 'lucide-react';
import AppEnvVarEditor from './AppEnvVarEditor';
import type { PreviewConfigFormState } from '../../../types/preview-environments';
import { getTemplateVarsForTrigger, MAX_CONCURRENT_PREVIEWS_LIMIT, DEFAULT_MAX_CONCURRENT_PREVIEWS } from '../../../utils/preview-constants';
import { validateNamespacePattern, sanitizeK8sNamespace, resolvePatternPreview, safeClipboardWrite } from '../../../utils/preview-utils';

const { Text } = Typography;

interface Props {
    selectedNodeId: string | null;
    formState: PreviewConfigFormState;
    updateFormState: (updates: Partial<PreviewConfigFormState>) => void;
    clusters?: Array<{ id: string; name: string; associatedNamespace?: string }>;
}

export default function PreviewNodeConfigPanel({ selectedNodeId, formState, updateFormState, clusters }: Props) {
    const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
    const [copiedVar, setCopiedVar] = useState<string | null>(null);
    const [appSearch, setAppSearch] = useState('');
    const namingInputRef = useRef<InputRef>(null);

    const toggleAppInclusion = (appName: string) => {
        const updatedApps = formState.apps.map(app =>
            app.appName === appName ? { ...app, included: !app.included } : app
        );
        updateFormState({ apps: updatedApps });
        const app = formState.apps.find(a => a.appName === appName);
        if (app?.included) {
            setExpandedApps(prev => {
                const next = new Set(prev);
                next.delete(appName);
                return next;
            });
        }
    };

    const toggleExpanded = (appName: string) => {
        setExpandedApps(prev => {
            const next = new Set(prev);
            if (next.has(appName)) {
                next.delete(appName);
            } else {
                next.add(appName);
            }
            return next;
        });
    };

    const updateAppEnvVars = (appName: string, envVars: Array<{ key: string; value: string }>) => {
        const updatedApps = formState.apps.map(app =>
            app.appName === appName ? { ...app, envVarOverrides: envVars } : app
        );
        updateFormState({ apps: updatedApps });
    };

    const updateAppFilePattern = (appName: string, filePattern: string) => {
        const updatedApps = formState.apps.map(app =>
            app.appName === appName ? { ...app, filePattern: filePattern || undefined } : app
        );
        updateFormState({ apps: updatedApps });
    };

    const insertOrCopyVar = (varName: string) => {
        const inputEl = namingInputRef.current?.input as HTMLInputElement | undefined;

        if (inputEl && document.activeElement === inputEl) {
            const start = inputEl.selectionStart ?? inputEl.value.length;
            const end = inputEl.selectionEnd ?? start;
            const currentVal = inputEl.value;
            const newVal = currentVal.slice(0, start) + varName + currentVal.slice(end);
            updateFormState({ namingPattern: newVal });
            setTimeout(() => {
                const pos = start + varName.length;
                inputEl.setSelectionRange(pos, pos);
                inputEl.focus();
            }, 0);
            return;
        }

        safeClipboardWrite(varName);
        setCopiedVar(varName);
        setTimeout(() => setCopiedVar(null), 1500);
    };

    const triggerEvents = formState.triggerEvents || ['PR'];
    const hasPR = triggerEvents.includes('PR');
    const hasTag = triggerEvents.includes('TAG');
    const hasTagOnly = hasTag && !hasPR;
    const hasBothTriggers = hasPR && hasTag;

    if (!selectedNodeId) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '40px 24px',
                textAlign: 'center'
            }}>
                <div style={{
                    width: 60,
                    height: 60,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #f3f4f6, #e5e7eb)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 16
                }}>
                    <Info size={28} style={{ color: '#9ca3af' }} />
                </div>
                <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 8, color: '#1f2937' }}>
                    Select a workflow step
                </Text>
                <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.6 }}>
                    Click on any node in the workflow to configure its settings
                </Text>
            </div>
        );
    }

    return (
        <div style={{ height: '100%', overflowY: 'auto' }}>
            {/* ── PR Event / Trigger ────────────────────────────────── */}
            {selectedNodeId === 'trigger' && (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Filter size={16} style={{ color: '#3b82f6' }} />
                            <Text strong style={{ fontSize: 15 }}>Trigger Events</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Configure which events trigger the preview workflow
                        </Text>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    <Form layout="vertical">
                        <Form.Item label={<Text strong style={{ fontSize: 13 }}>Trigger on</Text>}>
                            <Radio.Group
                                value={(formState.triggerEvents || ['PR'])[0]}
                                onChange={e => updateFormState({ triggerEvents: [e.target.value] })}
                            >
                                <Radio value="PR">Pull requests</Radio>
                                <Radio value="TAG">Git tags</Radio>
                            </Radio.Group>
                        </Form.Item>
                        {hasTag && (
                            <Form.Item
                                label={<Text strong style={{ fontSize: 13 }}>Tag pattern</Text>}
                                tooltip="Filter tags with glob patterns"
                            >
                                <Input
                                    value={formState.tagPattern || ''}
                                    onChange={e => updateFormState({ tagPattern: e.target.value })}
                                    placeholder="v*, release-*"
                                />
                                {hasBothTriggers && (
                                    <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                        Applies to tag triggers only
                                    </Text>
                                )}
                            </Form.Item>
                        )}
                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>Target branch</Text>}
                            tooltip={
                                hasBothTriggers
                                    ? "Target branch (applies to both PR and TAG triggers)"
                                    : hasTagOnly
                                        ? "Only trigger for tags created from this branch"
                                        : "Only trigger for PRs targeting this branch"
                            }
                        >
                            <Input
                                value={formState.targetBranch}
                                onChange={e => updateFormState({ targetBranch: e.target.value })}
                                placeholder="main"
                            />
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                {hasBothTriggers
                                    ? "Applies to both PR and TAG triggers"
                                    : hasTagOnly
                                        ? "Only trigger for tags created from this branch"
                                        : "PRs must target this branch to trigger previews"}
                            </Text>
                        </Form.Item>

                        {hasPR && (
                            <Form.Item
                                label={<Text strong style={{ fontSize: 13 }}>Branch pattern</Text>}
                                tooltip="Filter PR source branches using glob patterns"
                            >
                                <Input
                                    value={formState.branchPattern}
                                    onChange={e => updateFormState({ branchPattern: e.target.value })}
                                    placeholder="feature/*, fix/*, preview/*"
                                />
                                <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                    {hasBothTriggers
                                        ? "Applies to PR triggers only. Leave empty to match all branches"
                                        : "Leave empty to match all branches. Use glob patterns like feature/*"}
                                </Text>
                            </Form.Item>
                        )}
                    </Form>

                    <div style={{
                        marginTop: 8,
                        padding: '12px 14px',
                        background: '#f0f9ff',
                        borderRadius: 8,
                        border: '1px solid #bae6fd',
                    }}>
                        <Text style={{ fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
                            When a matching PR is <strong>opened or updated</strong>, the deploy path runs.
                            When it's <strong>closed or merged</strong>, the cleanup path runs.
                        </Text>
                    </div>
                </div>
            )}

            {/* ── Create Namespace ──────────────────────────────────── */}
            {selectedNodeId === 'namespace' && (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Box size={16} style={{ color: '#0ea5e9' }} />
                            <Text strong style={{ fontSize: 15 }}>Create Namespace</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Select the cluster and configure namespace naming for preview environments
                        </Text>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    <Form layout="vertical">
                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>Kubernetes cluster</Text>}
                            tooltip="Cluster where preview namespaces will be created"
                        >
                            <Select
                                value={formState.rootClusterId}
                                onChange={clusterId => updateFormState({ rootClusterId: clusterId })}
                                placeholder="Select a Kubernetes cluster"
                                options={(clusters || []).filter(c => !c.associatedNamespace).map(c => ({ value: c.id, label: c.name }))}
                                loading={!clusters}
                            />
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                Each preview gets its own isolated namespace in this cluster
                            </Text>
                        </Form.Item>

                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>Namespace pattern</Text>}
                            tooltip="Template for the K8s namespace. Supports MIC_ variables."
                        >
                            <Input
                                ref={namingInputRef}
                                value={formState.namingPattern}
                                onChange={e => updateFormState({ namingPattern: e.target.value })}
                                placeholder="preview-pr-{MIC_PR_NUMBER}"
                                status={formState.namingPattern ? (validateNamespacePattern(formState.namingPattern) ? 'error' : undefined) : undefined}
                            />
                            {formState.namingPattern && validateNamespacePattern(formState.namingPattern) && (
                                <Text style={{ fontSize: 12, marginTop: 6, display: 'block', color: '#ff4d4f' }}>
                                    {validateNamespacePattern(formState.namingPattern)}
                                </Text>
                            )}
                            {formState.namingPattern && !validateNamespacePattern(formState.namingPattern) && (
                                <div style={{ marginTop: 8, padding: '8px 10px', background: '#f0fdf4', borderRadius: 6, border: '1px solid #bbf7d0' }}>
                                    <Text style={{ fontSize: 11, color: '#6b7280', display: 'block', marginBottom: 2 }}>Resolved namespace preview:</Text>
                                    <Text code style={{ fontSize: 12 }}>
                                        {sanitizeK8sNamespace(resolvePatternPreview(formState.namingPattern, (formState.triggerEvents || []).includes('TAG')))}
                                    </Text>
                                </div>
                            )}
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                                    Available variables (click to insert or copy)
                                </Text>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {getTemplateVarsForTrigger(formState.triggerEvents).map(v => {
                                        const isCopied = copiedVar === v;
                                        return (
                                            <Tag
                                                key={v}
                                                aria-label={`Template variable ${v}`}
                                                style={{
                                                    cursor: 'pointer',
                                                    fontSize: 11,
                                                    margin: 0,
                                                    userSelect: 'none',
                                                    transition: 'all 0.15s',
                                                    background: isCopied ? '#f0fdf4' : undefined,
                                                    borderColor: isCopied ? '#86efac' : undefined,
                                                    color: isCopied ? '#16a34a' : undefined,
                                                }}
                                                onClick={() => insertOrCopyVar(v)}
                                            >
                                                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                                                    <span style={{ visibility: isCopied ? 'hidden' : 'visible' }}>{v}</span>
                                                    {isCopied && (
                                                        <span style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: 3,
                                                        }}>
                                                            <Check size={10} /> Copied
                                                        </span>
                                                    )}
                                                </span>
                                            </Tag>
                                        );
                                    })}
                                </div>
                            </div>
                        </Form.Item>
                    </Form>

                    <Divider style={{ margin: '20px 0' }} />

                    <Form layout="vertical">
                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>Max concurrent previews</Text>}
                            tooltip="Limits how many preview namespaces can exist simultaneously"
                        >
                            <InputNumber
                                min={1}
                                max={MAX_CONCURRENT_PREVIEWS_LIMIT}
                                value={formState.maxConcurrentPreviews}
                                onChange={(v) => updateFormState({ maxConcurrentPreviews: v || DEFAULT_MAX_CONCURRENT_PREVIEWS })}
                                style={{ width: '100%' }}
                            />
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                New previews are blocked when the limit is reached. Oldest previews can be cleaned up to make room.
                            </Text>
                        </Form.Item>
                    </Form>

                    <div style={{
                        marginTop: 8,
                        padding: '12px 14px',
                        background: '#ecfdf5',
                        borderRadius: 8,
                        border: '1px solid #a7f3d0',
                    }}>
                        <Text style={{ fontSize: 12, color: '#059669', lineHeight: 1.6 }}>
                            If the namespace already exists for this PR, this step is skipped and the workflow proceeds directly to deploying apps.
                        </Text>
                    </div>
                </div>
            )}

            {/* ── Deploy Apps ───────────────────────────────────────── */}
            {selectedNodeId === 'apps' && (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Layers size={16} style={{ color: '#f59e0b' }} />
                            <Text strong style={{ fontSize: 15 }}>Deploy Apps</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Select which apps to build and deploy into the preview namespace
                        </Text>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    {formState.apps.length > 0 && (
                        <Input
                            prefix={<Search size={14} style={{ color: '#9ca3af' }} />}
                            placeholder="Search apps..."
                            value={appSearch}
                            onChange={e => setAppSearch(e.target.value)}
                            allowClear
                            style={{ marginBottom: 12 }}
                        />
                    )}

                    {(() => {
                        const filteredApps = appSearch.trim()
                            ? formState.apps.filter(app => app.appName.toLowerCase().includes(appSearch.toLowerCase().trim()))
                            : formState.apps;

                        if (formState.apps.length === 0) {
                            return (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        <Text type="secondary">
                                            No apps found in the project
                                        </Text>
                                    }
                                />
                            );
                        }

                        if (filteredApps.length === 0) {
                            return (
                                <div style={{
                                    border: '1px dashed #d1d5db',
                                    borderRadius: 8,
                                    padding: '20px 14px',
                                    textAlign: 'center',
                                }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        No apps matching &ldquo;{appSearch}&rdquo;
                                    </Text>
                                </div>
                            );
                        }

                        return (
                        <div style={{
                            background: '#fafafa',
                            borderRadius: 8,
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                        }}>
                            {filteredApps.map((app, index) => {
                                const isExpanded = expandedApps.has(app.appName);
                                const overrideCount = (app.envVarOverrides || []).length;

                                return (
                                    <div key={app.appName}>
                                        {index > 0 && (
                                            <div style={{ height: 1, background: '#e5e7eb' }} />
                                        )}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '10px 12px',
                                            gap: 8,
                                        }}>
                                            <Checkbox
                                                checked={app.included}
                                                onChange={() => toggleAppInclusion(app.appName)}
                                            />
                                            <Space size={8} style={{ flex: 1, minWidth: 0 }}>
                                                <Container size={14} style={{ color: '#097BB5', flexShrink: 0 }} />
                                                <Text strong style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {app.appName}
                                                </Text>
                                                {overrideCount > 0 && (
                                                    <Text type="secondary" style={{ fontSize: 11, flexShrink: 0 }}>
                                                        {overrideCount} var{overrideCount !== 1 ? 's' : ''}
                                                    </Text>
                                                )}
                                            </Space>
                                            {app.included && (
                                                <div
                                                    onClick={() => toggleExpanded(app.appName)}
                                                    style={{
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: 4,
                                                        flexShrink: 0,
                                                        color: '#6b7280',
                                                    }}
                                                >
                                                    {isExpanded
                                                        ? <ChevronDown size={16} />
                                                        : <ChevronRight size={16} />
                                                    }
                                                </div>
                                            )}
                                        </div>
                                        {app.included && isExpanded && (
                                            <div style={{
                                                padding: '12px',
                                                background: '#fff',
                                                borderTop: '1px solid #f0f0f0',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 12,
                                            }}>
                                                {/* File Pattern Section */}
                                                <div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        marginBottom: 8,
                                                        paddingBottom: 6,
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}>
                                                        <Filter size={12} style={{ color: '#6b7280' }} />
                                                        <Text strong style={{ fontSize: 12, color: '#374151' }}>
                                                            File Pattern
                                                        </Text>
                                                        <Tooltip title="Comma-separated glob patterns. Only rebuilds this app on PR sync when matched files change. Leave empty to always rebuild.">
                                                            <Info size={11} style={{ color: '#9ca3af', cursor: 'help' }} />
                                                        </Tooltip>
                                                    </div>
                                                    <Input
                                                        size="small"
                                                        value={app.filePattern || ''}
                                                        onChange={e => updateAppFilePattern(app.appName, e.target.value)}
                                                        placeholder="apps/frontend/**,libs/shared/**"
                                                    />
                                                </div>

                                                {/* Environment Variable Overrides Section */}
                                                <div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                        marginBottom: 8,
                                                        paddingBottom: 6,
                                                        borderBottom: '1px solid #f0f0f0',
                                                    }}>
                                                        <GitPullRequest size={12} style={{ color: '#6b7280' }} />
                                                        <Text strong style={{ fontSize: 12, color: '#374151' }}>
                                                            Environment Variable Overrides
                                                        </Text>
                                                    </div>
                                                    <AppEnvVarEditor
                                                        envVars={app.envVarOverrides || []}
                                                        onChange={(envVars) => updateAppEnvVars(app.appName, envVars)}
                                                        triggerEvents={formState.triggerEvents}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        );
                    })()}

                </div>
            )}

            {/* ── Remove Preview / Cleanup ─────────────────────────── */}
            {selectedNodeId === 'cleanup' && (
                <div>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <Trash2 size={16} style={{ color: '#ef4444' }} />
                            <Text strong style={{ fontSize: 15 }}>Remove Preview</Text>
                        </div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Configure what happens when a PR is closed or merged
                        </Text>
                    </div>

                    <Divider style={{ margin: '16px 0' }} />

                    <Form layout="vertical">
                        <Form.Item
                            label={<Text strong style={{ fontSize: 13 }}>Auto-destroy on PR close</Text>}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '10px 14px',
                                background: '#ffffff',
                                borderRadius: 6,
                                border: '1px solid #e8e8e8',
                            }}>
                                <Text style={{ fontSize: 13, fontWeight: 500 }}>
                                    {formState.autoDestroyOnPRClose ? 'Enabled' : 'Disabled'}
                                </Text>
                                <Switch
                                    checked={formState.autoDestroyOnPRClose}
                                    onChange={(autoDestroyOnPRClose) => updateFormState({ autoDestroyOnPRClose })}
                                />
                            </div>
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                                When enabled, the preview namespace and all deployed apps are automatically deleted when the PR is closed or merged.
                            </Text>
                        </Form.Item>
                    </Form>

                    <div style={{
                        marginTop: 8,
                        padding: '12px 14px',
                        background: '#fef2f2',
                        borderRadius: 8,
                        border: '1px solid #fecaca',
                    }}>
                        <Text strong style={{ fontSize: 12, color: '#991b1b', display: 'block', marginBottom: 4 }}>
                            What gets removed:
                        </Text>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                            <li><Text style={{ fontSize: 12, color: '#991b1b' }}>Kubernetes namespace</Text></li>
                            <li><Text style={{ fontSize: 12, color: '#991b1b' }}>All deployed apps and services</Text></li>
                            <li><Text style={{ fontSize: 12, color: '#991b1b' }}>Associated resources (ConfigMaps, Secrets, etc.)</Text></li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}
